import type { CameraOption, RatioOption, StyleOption, VideoModelOption } from '../types'

export const promptPools: Record<string, string[]> = {
  'cinematic trending': [
    '电影级镜头缓慢推进，穿过雨后的城市街道，霓虹灯光在地面水洼中倒映，色调偏青蓝冷调，氛围感十足',
    '黄昏时分，镜头从高空缓缓俯拍，夕阳余晖洒在起伏的山脉上，云海翻涌，光影层次丰富',
    '慢镜头特写，一颗水滴从花瓣尖端坠落，背景虚化，自然光线透过花瓣形成光晕',
    '午夜的东京街头，行人匆匆走过十字路口，镜头以延时摄影的方式捕捉城市的脉动',
    '海边日出，金色光芒逐渐铺满海面，浪花轻柔地拍打沙滩，镜头从低角度仰拍',
    '森林中的小溪缓缓流淌，阳光透过树叶的缝隙洒下斑驳的光影，氛围宁静而治愈',
    '繁华都市的航拍画面，车流灯光形成丝绸般的光带，夜色璀璨',
    '一列复古火车穿越雪山隧道，蒸汽在冷空气中升腾，画面充满怀旧质感',
  ],
  'news government official': [
    '庄严的新闻播报场景，主持人端坐在播报台前，背景是数字地球和动态数据图表',
    '政务大厅内，工作人员正在为市民办理业务，画面明亮简洁，体现服务型政府形象',
    '城市规划沙盘的航拍俯视图，建筑模型整齐排列，镜头缓缓推近标志性建筑',
    '绿色能源政策宣传画面，风力发电机组在蓝天白云下旋转，象征可持续发展',
    '现代化的智慧城市全景，高楼大厦间穿梭着自动驾驶汽车，科技感十足',
    '会议室中进行视频连线，屏幕上显示各地代表发言，展现高效治理',
    '乡村振兴场景，丰收的稻田金黄一片，农民在田间劳作，远处是焕然一新的村庄',
    '城市河道治理成果展示，曾经的黑臭水体变成清澈的景观河，两岸绿树成荫',
  ],
  'education science documentary': [
    '宇宙深空画面，镜头穿越星云和行星带，展现浩瀚宇宙的壮美景象',
    '显微镜下的细胞分裂过程，微观世界中生命的诞生充满奇迹感',
    '教室里学生们认真听课，阳光透过窗户洒在课本上，营造温馨的学习氛围',
    '化学实验室中，五彩斑斓的液体在烧杯中呈现出奇妙的反应，科学之美',
    '自然纪录片风格，一只雄鹰展翅翱翔在雪山之巅，镜头以航拍视角跟随',
    '人体神经系统的三维可视化动画，电信号沿神经纤维快速传导，科技质感',
    '深海探索画面，潜水器灯光照亮未知的海底，发光水母和深海鱼群出现在镜头中',
    '图书馆的长廊，书架延伸至远方，暖黄的灯光营造出知识殿堂的氛围',
  ],
  'product commercial advertisement': [
    '高端化妆品特写，瓶身在旋转中折射出流光溢彩的光泽，背景纯净简约',
    '咖啡从壶中缓缓倒入杯中的慢镜头，泡沫翻涌升腾，暖色调灯光营造氛围',
    '运动鞋在灯光下缓慢旋转展示，每一个细节纹理清晰可见，动感十足',
    '新款手机悬浮在半空中360度旋转，金属质感和屏幕画面交替展示',
    '美食广告画面，新鲜食材在空中翻转落入锅中，水花四溅，色彩鲜艳诱人',
    '汽车在雨天公路上疾驰，轮胎溅起水花，车灯划破雨幕，画面充满力量感',
    '珠宝首饰在黑色丝绸背景上缓慢旋转，钻石折射出璀璨光芒',
    '极简主义家具展示，阳光从落地窗照入，光影在空间中变幻，格调高雅',
  ],
  'creative art abstract': [
    '抽象流体艺术动画，彩色颜料在液体中扩散交融，形成梦幻般的图案',
    '万花筒般的几何图形不断变换组合，色彩从冷色调渐变为暖色调，充满艺术感',
    '数字粒子特效，无数光点在黑暗中聚合成人形后再次散开，如同星尘',
    '水墨在宣纸上晕染扩散的过程，黑白灰层次分明，兼具传统与现代美感',
    '光影雕塑作品在旋转中展示，光线穿过镂空的金属结构投射出变幻的阴影',
    '超现实风格场景，巨大的鲸鱼在城市楼宇间缓缓游动，周围漂浮着水泡',
    '油画笔触动画，梵高风格的星空逐笔绘成，画面充满生动的运动感',
    '赛博朋克风格的霓虹都市，全息广告在雨夜中闪烁，未来感满满',
  ],
}

export const styleSuffixes: Record<string, string> = {
  default: '',
  cinematic: '，电影级画面，色彩分级，浅景深，film grain，cinematic lighting',
  anime: '，日本动漫风格，精致的人物绘制，富有表现力的色彩，anime style',
  ink: '，中国水墨画风格，笔墨纹理，留白构图，意境深远，Chinese ink painting',
  cyberpunk: '，赛博朋克风格，霓虹灯光，雨夜城市，科技感，cyberpunk neon',
  oil: '，油画笔触质感，厚重色彩，印象派光影，oil painting texture',
  '3d': '，3D渲染风格，光滑材质，柔和全局光照，blender render',
  vintage: '，胶片摄影风格，暖色调色温，粒子感，微微过曝，vintage film',
}

export const cameraPrefixes: Record<string, string> = {
  free: '',
  fixed: '',
  push: 'Slow push in, ',
  pull: 'Slowly pull out, ',
  orbit: 'Orbit shot around the subject, ',
  aerial: 'Aerial top-down view slowly descending, ',
  track: 'Tracking shot following the subject, ',
}

export const cameraFixed: Record<string, boolean> = {
  free: false,
  fixed: true,
  push: false,
  pull: false,
  orbit: false,
  aerial: false,
  track: false,
}

export const tabQueries: Record<string, string> = {
  hot: 'cinematic trending',
  policy: 'news government official',
  edu: 'education science documentary',
  product: 'product commercial advertisement',
  more: 'creative art abstract',
}

export const styles: StyleOption[] = [
  { key: 'default', label: '默认', icon: '🎞', bg: '#f1f5f9' },
  { key: 'cinematic', label: '电影质感', icon: '🎬', bg: '#fffbeb' },
  { key: 'anime', label: '动漫风', icon: '🌸', bg: '#fdf2f8' },
  { key: 'ink', label: '水墨画', icon: '🖌', bg: '#f5f5f4' },
  { key: 'cyberpunk', label: '赛博朋克', icon: '🌆', bg: '#f5f3ff' },
  { key: 'oil', label: '油画风', icon: '🎨', bg: '#fff7ed' },
  { key: '3d', label: '3D 渲染', icon: '💎', bg: '#ecfeff' },
  { key: 'vintage', label: '胶片复古', icon: '📷', bg: '#fefce8' },
]

export const ratios: RatioOption[] = [
  { value: '21:9', boxW: 24, boxH: 12 },
  { value: '16:9', boxW: 24, boxH: 14 },
  { value: '4:3', boxW: 20, boxH: 16 },
  { value: '1:1', boxW: 16, boxH: 16 },
  { value: '3:4', boxW: 14, boxH: 20 },
  { value: '9:16', boxW: 12, boxH: 24 },
]

export const durations: string[] = ['5s', '10s', '12s']

export const cameras: CameraOption[] = [
  { key: 'free', label: '自由运镜', icon: 'fa-solid fa-arrows-up-down-left-right' },
  { key: 'fixed', label: '固定镜头', icon: 'fa-solid fa-lock' },
  { key: 'push', label: '缓慢推进', icon: 'fa-solid fa-up-long' },
  { key: 'pull', label: '平滑拉远', icon: 'fa-solid fa-down-long' },
  { key: 'orbit', label: '环绕拍摄', icon: 'fa-solid fa-rotate' },
  { key: 'aerial', label: '航拍俯瞰', icon: 'fa-solid fa-helicopter' },
  { key: 'track', label: '平移跟随', icon: 'fa-solid fa-person-walking' },
]

export const videoModels: VideoModelOption[] = [
  { key: 'doubao-seedance-2-0-260128', label: 'Seedance 2.0', desc: '最新一代，画质最优' },
  { key: 'doubao-seedance-1-5-pro-251215', label: 'Seedance 1.5 Pro', desc: '支持音画同步' },
  { key: 'doubao-seedance-1-0-pro-250528', label: 'Seedance 1.0 Pro', desc: '高质量标准版' },
  { key: 'doubao-seedance-1-0-pro-fast-251015', label: 'Seedance 1.0 Fast', desc: '速度最快，成本更低' },
]

export const fallbackImgs: string[] = [
  'https://images.unsplash.com/photo-1557683316-973673baf926?w=400',
  'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400',
  'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?w=400',
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400',
  'https://images.unsplash.com/photo-1526628953301-3e589a6a8b74?w=400',
  'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400',
  'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400',
  'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=400',
]
